import { onlyUnique } from '../../../../../util/arrays/filter';

/**
 * gera os nodes principais (as bolinhas, no gráfico, que representam
 * as alternativas principais de uma pergunta)
 * @param {*} payload resultado da correlação
 * @param {*} categoryIndexFetcher função que busca o índice de uma categoria
 * baseado na pergunta
 * @param {*} sizeCalculator calcula o tamanho do nós (bolinha) baseado
 * na alternativa que o nó representa
 * @param {*} bindMetadata função auxiliar que vincula dados customizados no
 * node (bolinha), no campo "metadata".
 */
function generateMainNodes(payload, categoryIndexFetcher, sizeCalculator,
  bindMetadata) {
  const mainNodes = payload
    .alternativas
    .map((alternativa) => ({
      category: categoryIndexFetcher(payload),
      name: alternativa.enunciado,
      symbolSize: sizeCalculator(alternativa),
      metadata: bindMetadata(payload, alternativa),
    }));
  return mainNodes;
}

/**
 * gera todos nós correlacionados (que girarão em torno
 * da alternativa principal)
 *
 * @param {*} payload dados que devem ser plotados no gráfico
 * @param {*} categoryIndexFetcher recupera o índice da categoria
 * do nó de acordo com o nome a pergunta da alternativa sendo exibida
 * (esta categoria é o que define a cor que a bolinha será)
 * @param {*} sizeCalculator calcula o tamanho da bolinha baseado num
 * contexto, que é a alternativa
 * @param {*} bindMetadata vincula metadados ao nós (bolinha) sendo exibido
 * no gráfico. Esta função é util para caso deseje-se obter metadados do nó
 * em tempo de execução, como por ex, para exibir dados adicionais num tooltip
 */
function generateCorrelatedNodes(
  payload, categoryIndexFetcher, sizeCalculator, bindMetadata,
) {
  const correlatedNodes = payload.alternativas
    .map((alternativa, index) => alternativa.perguntasCorrelacionadas
      .map((pergunta) => ({
        parent: {
          index,
          alternativa: {
            id: alternativa.id,
            enunciado: alternativa.enunciado,
          },
        },
        ...pergunta,
      })))
    .reduce((reduced, next) => [...reduced, ...next], [])
    .map((pergunta) => {
      const categoryIndex = categoryIndexFetcher(pergunta);

      const nodes = pergunta.alternativas
        .map((alternativa) => ({
          parent: pergunta.parent.index,
          category: categoryIndex,
          name: alternativa.enunciado,
          symbolSize: sizeCalculator(alternativa),
          metadata: bindMetadata(pergunta, alternativa),
        }));

      return nodes;
    })
    .reduce((reduced, next) => [...reduced, ...next], []);

  return correlatedNodes;
}

/**
 * identifica as categorias presentes no resultado
 * de uma correlação.
 *
 * Esta implementação utiliza o enunciado das perguntas como categorias
 *
 * @param {*} payload resultado da correção
 * @return retorna uma array onde a primeira posição possui uma função
 * auxiliar que recupera o índice da categoria baseado num conteudo textual
 * (identificador da categoria) e a segunda posição possui um array com todas
 * as categorias identificadas.
 */
function generateCategories(payload) {
  const targetQuestions = payload
    .alternativas.map((alternativa) => alternativa.perguntasCorrelacionadas)
    .reduce((reduced, next) => [...reduced, ...next], []) // flat map
    .map((pergunta) => pergunta.enunciado)
    .filter(onlyUnique);

  const allQuestions = [
    payload.enunciado,
    ...targetQuestions,
  ];

  const categoryIndexByName = allQuestions.map((enunciado, index) => {
    const entry = {};
    entry[enunciado] = index;
    return entry;
  })
    .reduce((reduced, next) => ({ ...reduced, ...next }), {});

  const categories = allQuestions.map((enunciado) => ({
    name: enunciado,
    keyword: {},
    base: enunciado,
  }));

  return [(name) => categoryIndexByName[name], categories, targetQuestions];
}

/**
 * Gera os relacionamentos entre os nós principais (mainNodes | bolinhas
 * principais | alternativas principais) e os nós correlacionados (correlated
 * nodes | bolinhas correlacionadas | alternativas correlacionadas)
 *
 */
function generateLinks(mainNodes, correlatedNodes) {
  const mainNodesSize = mainNodes.length;
  const links = correlatedNodes
    .map((node, index) => ({
      source: node.parent,
      target: index + mainNodesSize,
    }));
  return links;
}

/**
 * converte o resultado de correlação para uma estrutura de dados
 * que o echarts entende.
 *
 * @param payload resultado da correlação
 *
 * expected payload structure:
 *
 *
 * {
 *   id: number;
 *   enunciado: string;
 *   alternativas: [
 *      {
 *        id: number;
 *        enunciado: string;
 *        perguntasCorrelacionadas: [
 *         {
 *            id: number;
 *            enunciado: string;
 *            alternativas: [
 *               {
 *                  id: number;
 *                  enunciado: string;
 *                  correlacao: number;
 *               }
 *            ]
 *         }
 *       ]
 *      }
 *   ]
 * }
 */
export default function toEchartData(payload) {
  const [getCategoryByName, categories, legends] = generateCategories(payload);

  const mainNodes = generateMainNodes(
    payload,
    (pergunta) => getCategoryByName(pergunta.enunciado),
    (alternativa) => 15,
    (pergunta, alternativa) => ({
      pergunta: pergunta.enunciado,
      alternativa: alternativa.enunciado,
    }),
  );

  const correlatedNodes = generateCorrelatedNodes(
    payload,
    (pergunta) => getCategoryByName(pergunta.enunciado),
    (alternativa) => Math.abs(alternativa.correlacao) * 30,
    (pergunta, alternativa) => ({
      pergunta: pergunta.enunciado,
      alternativa: alternativa.enunciado,
      correlacao: alternativa.correlacao,
    }),
  );

  const nodes = [...mainNodes, ...correlatedNodes]
    .map((node, index) => ({
      id: index,
      ...node,
    }));

  const links = generateLinks(mainNodes, correlatedNodes);

  return {
    type: 'force',
    legends,
    categories,
    nodes,
    links,
  };
}
